import { a4PrintConfig } from "./commonConfig";

let printFrame = null;
let cachedPageStyle = null;
let cachedAppCss = null;
let stylesInjected = false;

const extractSameOriginCss = () => {
  if (typeof document === "undefined") return "";
  const parts = [];
  for (const sheet of document.styleSheets) {
    try {
      const rules = [...(sheet.cssRules ?? [])]
        .map((rule) => rule.cssText)
        .join("\n");
      if (rules) parts.push(rules);
    } catch {
      // Skip cross-origin stylesheets.
    }
  }
  return parts.join("\n");
};

const getCachedAppCss = () => {
  if (cachedAppCss !== null) return cachedAppCss;
  cachedAppCss = extractSameOriginCss();
  return cachedAppCss;
};

const getCachedPageStyle = (pageStyle = a4PrintConfig) => {
  if (cachedPageStyle !== null) return cachedPageStyle;
  cachedPageStyle = pageStyle;
  return cachedPageStyle;
};

const clonePrintNode = (node) => {
  const clone = node.cloneNode(true);
  const sourceImgs = node.querySelectorAll("img");
  const cloneImgs = clone.querySelectorAll("img");
  cloneImgs.forEach((img, index) => {
    const source = sourceImgs[index];
    if (!source) return;
    img.decoding = "sync";
    img.loading = "eager";
    const src = source.currentSrc || source.src;
    if (src) img.src = src;
  });
  return clone;
};

const areSourceImagesReady = (node) => {
  const imgs = node.querySelectorAll("img");
  if (!imgs.length) return true;
  return [...imgs].every(
    (img) => img.complete && (img.naturalWidth > 0 || img.naturalHeight > 0),
  );
};

const ensurePrintFrameReady = (doc, pageStyle = a4PrintConfig) => {
  if (!doc.getElementById("fast-print-app-css")) {
    const appCssEl = doc.createElement("style");
    appCssEl.id = "fast-print-app-css";
    appCssEl.textContent = getCachedAppCss();
    doc.head.appendChild(appCssEl);
  }

  let styleEl = doc.getElementById("fast-print-page-style");
  if (!styleEl) {
    styleEl = doc.createElement("style");
    styleEl.id = "fast-print-page-style";
    doc.head.appendChild(styleEl);
  }
  if (!stylesInjected) {
    styleEl.textContent = getCachedPageStyle(pageStyle);
    stylesInjected = true;
  }
};

const getPrintDocument = (pageStyle = a4PrintConfig) => {
  if (printFrame?.contentDocument) {
    return {
      iframe: printFrame,
      doc: printFrame.contentDocument,
    };
  }

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  Object.assign(iframe.style, {
    position: "fixed",
    width: "0",
    height: "0",
    border: "0",
    right: "0",
    bottom: "0",
    visibility: "hidden",
    pointerEvents: "none",
  });
  document.body.appendChild(iframe);
  printFrame = iframe;

  const doc = iframe.contentDocument;
  doc.open();
  doc.write(
    '<!DOCTYPE html><html><head><meta charset="utf-8"><title></title></head><body></body></html>',
  );
  doc.close();

  ensurePrintFrameReady(doc, pageStyle);

  return { iframe, doc };
};

const syncPrintContent = (node, doc) => {
  if (!node) return;
  doc.body.replaceChildren(clonePrintNode(node));
};

export const warmPrintFrame = (node, pageStyle = a4PrintConfig) => {
  if (!node || typeof window === "undefined") return;
  const { doc } = getPrintDocument(pageStyle);
  ensurePrintFrameReady(doc, pageStyle);
  syncPrintContent(node, doc);
};

export const printFromNode = (
  node,
  { title = "", pageStyle = a4PrintConfig } = {},
) => {
  if (!node || typeof window === "undefined") return;

  const previousTitle = document.title;
  if (title !== undefined) document.title = title;

  const { iframe, doc } = getPrintDocument(pageStyle);
  doc.title = title || "";
  ensurePrintFrameReady(doc, pageStyle);
  syncPrintContent(node, doc);

  const win = iframe.contentWindow;
  const invokePrint = () => {
    win.focus();
    win.print();
    document.title = previousTitle;
  };

  if (areSourceImagesReady(node)) {
    invokePrint();
    return;
  }

  const images = Array.from(doc.images ?? []);
  if (!images.length) {
    invokePrint();
    return;
  }

  let printed = false;
  const finish = () => {
    if (printed) return;
    printed = true;
    invokePrint();
  };

  const deadline = window.setTimeout(finish, 100);

  let loaded = 0;
  const markLoaded = () => {
    loaded += 1;
    if (loaded >= images.length) {
      window.clearTimeout(deadline);
      finish();
    }
  };

  images.forEach((img) => {
    if (img.complete) markLoaded();
    else {
      img.onload = markLoaded;
      img.onerror = markLoaded;
    }
  });
};
